// src/app/services/gallery.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface GalleryImage {
  id?: string;
  cardId: string;
  imageUrl: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AddGalleryImagesRequest {
  cardId: string;
  galleryUrls: string[];
}

export interface RemoveGalleryImageRequest {
  imageUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class GalleryService {
  private readonly baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.idToken;
    if (!token) {
      throw new Error('User not authenticated');
    }
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Add gallery images to a card
   * POST /api/gallery
   */
  addGalleryImages(cardId: string, galleryUrls: string[]): Observable<GalleryImage[]> {
    const request: AddGalleryImagesRequest = { cardId, galleryUrls };
    return this.http.post<GalleryImage[]>(
      `${this.baseUrl}/gallery`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Fetch gallery images for a card
   * GET /api/gallery/:cardId
   */
  getGalleryImages(cardId: string): Observable<GalleryImage[]> {
  return this.http.get<string[]>(
    `${this.baseUrl}/gallery/${cardId}`,
    { headers: this.getAuthHeaders() }
  ).pipe(
    map((urls: string[]) =>
      urls.map(url => ({
        cardId,
        imageUrl: url
      }))
    )
  );
}


  /**
   * Delete a single image from gallery
   * DELETE /api/gallery/:cardId/image
   */
  // src/app/services/gallery.service.ts
removeGalleryImage(cardId: string, imageUrl: string): Observable<void> {
  const url = `${this.baseUrl}/gallery/${cardId}/image`;

  return this.http.request<void>('delete', url, {
    headers: this.getAuthHeaders(),
    body: { imageUrl },          // <-- JSON body is always sent
  });
}

  /**
   * Delete all gallery images for a card
   * DELETE /api/gallery/:cardId
   */
  deleteAllGalleryImages(cardId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/gallery/${cardId}`,
      { headers: this.getAuthHeaders() }
    );
  }
}